import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { notify, confirmAsync } from '../utils/notify';
import './EmailVerificationModal.css';

function EmailVerificationModal({ email, onVerify, onResend, onClose }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(15 * 60); // 15 minutes in seconds

  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (code.length !== 6) {
      setError(t('registration.verification.code_error'));
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/api/v1/auth/verify-email', {
        email,
        code
      });

      if (response.data.success) {
        onVerify(response.data.user);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('registration.verification.code_invalid');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError('');
    try {
      await apiClient.post('/api/v1/auth/resend-verification-code', { email });
      setTimeRemaining(15 * 60); // Reset timer
      notify.success(t('registration.verification.resend_sent'));
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      // Check if it's the resend_limit error
      if (errorDetail === 'resend_limit') {
        setError(t('registration.verification.resend_limit'));
      } else {
        const errorMessage = errorDetail || t('registration.verification.resend_error');
        setError(errorMessage);
      }
    } finally {
      setResendLoading(false);
    }
  };

  const maskEmail = (email) => {
    const [local, domain] = email.split('@');
    const maskedLocal = local.slice(0, 2) + '***' + local.slice(-1);
    return `${maskedLocal}@${domain}`;
  };

  const handleClose = async () => {
    // Warn user before closing - they can still close but will be reminded they can return
    if (await confirmAsync({ message: t('registration.verification.close_warning') })) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>×</button>
        <h2>{t('registration.verification.title')}</h2>
        <p className="verification-subtitle">
          {t('registration.verification.subtitle')} {maskEmail(email)}
        </p>

        {timeRemaining > 0 ? (
          <p className="expiration-time">
            {t('registration.verification.expires_in')}: <strong>{formatTime(timeRemaining)}</strong>
          </p>
        ) : (
          <p className="expiration-time expired">
            {t('registration.verification.code_expired')}
          </p>
        )}

        <form onSubmit={handleVerify}>
          <div className="code-input-group">
            <label>{t('registration.verification.code_input')}</label>
            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder={t('registration.verification.code_placeholder')}
              className="code-input"
              maxLength="6"
              autoFocus
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="verify-button"
            disabled={loading || code.length !== 6 || timeRemaining === 0}
          >
            {loading ? t('registration.verification.submit_verifying') : t('registration.verification.submit')}
          </button>
        </form>

        <div className="resend-section">
          <p>{t('registration.verification.resend')}</p>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendLoading}
            className="resend-button"
          >
            {resendLoading ? '...' : t('registration.verification.resend_button')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationModal;

